import { Link } from "react-router-dom";
import { useSeo } from "@/lib/seo";
import { IMAGES } from "@/data/site";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import LeadForm from "@/components/LeadForm";
import { Button } from "@/components/ui/button";
import { Zap, Droplets, Waves, Flame, Cable, Home, Sprout, Users, Building, ClipboardCheck, FileDown, MapPin, Image as ImageIcon } from "lucide-react";

const SECTIONS = [
  { icon: Zap, key: "power", title: "Power", body: "300+ MW is a long-term Path to Power goal — not confirmed utility capacity. It is subject to utility studies, approvals, and power delivery. The site is positioned near existing transmission, and power delivery pathways and substation expansion are under active evaluation." },
  { icon: Droplets, key: "water", title: "Water", body: "Municipal and permitted water sourcing strategies are being coordinated to support high-density technology loads and community growth." },
  { icon: Waves, key: "wastewater", title: "Wastewater", body: "Wastewater capacity and treatment modernization are being evaluated as part of a broader utility improvement plan." },
  { icon: Flame, key: "gas", title: "Natural Gas", body: "Natural gas availability is being assessed for backup generation and industrial process potential." },
  { icon: Cable, key: "fiber", title: "Fiber", body: "Fiber backbone access and redundant routing are central to the campus's technology-driven positioning." },
  { icon: Home, key: "housing", title: "Housing", body: "Entitled residential and workforce housing components are planned to support new employment centers and regional growth." },
  { icon: Sprout, key: "agritech", title: "Agri-Tech", body: "Hydroponics, controlled-environment agriculture and heat reuse loops integrate sustainable food production with campus energy systems." },
  { icon: Users, key: "community", title: "Community Benefits", body: "Potential community benefits may include infrastructure improvements, employment, education and training, and utility modernization. Projects may be structured to support long-term local economic development." },
  { icon: Building, key: "commercial", title: "Commercial & Education", body: "Commercial development and education / training opportunities are envisioned to round out a complete, mixed-use community." },
  { icon: ClipboardCheck, key: "status", title: "Project Status", body: "Site Controlled. Utility diligence, entitlements and capital formation activities are ongoing." },
];

// Placeholder imagery — replaced with real Metter aerial photography when provided.
const GALLERY = [
  { url: IMAGES.transmission, caption: "Transmission corridor" },
  { url: IMAGES.aerialLand, caption: "Aerial development land" },
  { url: IMAGES.substation, caption: "Substation infrastructure" },
  { url: IMAGES.greenhouse, caption: "Agri-tech greenhouse" },
  { url: IMAGES.dataCenter, caption: "Data center infrastructure" },
  { url: IMAGES.residential, caption: "Workforce housing" },
  { url: IMAGES.warehouse, caption: "Industrial / commercial" },
  { url: IMAGES.substationNight, caption: "Utility modernization" },
];

export default function MetterFirst() {
  useSeo("Project Metter First — Metter, Georgia | 12 Stone Development Group",
    "Project Metter First is a large-scale integrated development vision in Metter, Georgia combining data center infrastructure, workforce housing, agri-tech, hydroponics, heat reuse and community growth.");
  return (
    <div>
      <PageHero overline="Featured Development · Metter, Georgia" title="Project Metter First"
        subtitle="A large-scale integrated development vision where data center infrastructure, housing, agri-tech and community growth are developed together."
        image={IMAGES.transmission} height="min-h-[70vh]" />

      <section className="max-w-4xl mx-auto px-4 sm:px-6 py-20">
        <Reveal>
          <div className="overline mb-3">Vision</div>
          <p className="text-lg text-foreground/90 leading-relaxed">
            Project Metter First reimagines how growth happens. Rather than developing land, power, housing and
            technology in isolation, 12 Stone is coordinating them within a single integrated campus in Metter, Georgia —
            incorporating data center infrastructure, residential and workforce housing, agri-tech, hydroponics,
            utility modernization, heat reuse, community infrastructure, commercial development, and education and
            training opportunities.
          </p>
          <p className="mt-5 text-sm text-muted-foreground italic">
            Confidential pricing, seller names and protected information are not published. Details are shared with
            qualified partners under appropriate confidentiality.
          </p>
          <Button asChild className="mt-7 bg-primary hover:bg-primary/90 text-white font-semibold rounded-full px-6" data-testid="metter-download-summary">
            <a href="/project-metter-first-summary.pdf" target="_blank" rel="noreferrer"><FileDown className="mr-2 h-4 w-4" /> DOWNLOAD PROJECT SUMMARY</a>
          </Button>
        </Reveal>
      </section>

      {/* GALLERY */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6">
        <Reveal>
          <div className="overline mb-3 flex items-center gap-2"><ImageIcon className="h-3.5 w-3.5" /> Aerial & Site Imagery</div>
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-foreground mb-6">Site Gallery</h2>
        </Reveal>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {GALLERY.map((g, i) => (
            <Reveal key={i} delay={(i % 4) * 0.05}>
              <div className="relative rounded-xl overflow-hidden border border-border aspect-[4/3] group">
                <img src={g.url} alt={g.caption} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-primary/90 to-transparent p-3">
                  <span className="font-mono text-[10px] uppercase tracking-wider text-gray-300">{g.caption}</span>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
        <p className="mt-4 text-xs text-muted-foreground italic">Representative infrastructure imagery. Site-specific aerial photography to be added.</p>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-20">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {SECTIONS.map((s, i) => (
            <Reveal key={s.key} delay={(i % 2) * 0.06}>
              <div id={s.key} className="rounded-xl border border-border bg-card p-7 h-full">
                <div className="flex items-center gap-3 mb-3">
                  <div className="h-10 w-10 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center text-primary"><s.icon className="h-5 w-5" /></div>
                  <h3 className="font-display text-xl font-semibold text-foreground">{s.title}</h3>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed">{s.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* SITE MAP */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-20">
        <Reveal>
          <div className="overline mb-3 flex items-center gap-2"><MapPin className="h-3.5 w-3.5" /> Location</div>
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-foreground mb-6">Site Map — Metter, Georgia</h2>
          <div className="rounded-xl overflow-hidden border border-border aspect-video">
            <iframe title="Project Metter First location" src="https://www.google.com/maps?q=Metter,+Georgia&output=embed" width="100%" height="100%" style={{ border: 0 }} loading="lazy" referrerPolicy="no-referrer-when-downgrade"></iframe>
          </div>
          <p className="mt-4 text-xs text-muted-foreground italic">General project vicinity. Parcel-level mapping shared with qualified partners under confidentiality.</p>
        </Reveal>
      </section>

      <section className="border-t border-border bg-secondary">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-20">
          <Reveal>
            <div className="overline mb-3 text-center">Partner Inquiries</div>
            <h2 className="font-display text-3xl font-bold text-foreground text-center text-balance">Explore participation in Project Metter First</h2>
            <p className="text-muted-foreground text-center mt-3 mb-8">Qualified data center operators, utilities, investors and development partners are invited to inquire.</p>
            <div className="rounded-xl border border-border bg-card p-6 sm:p-8">
              <LeadForm leadType="partner" defaultCategory="Project Metter First" submitLabel="INQUIRE ABOUT METTER FIRST" testId="metter-inquiry" />
            </div>
            <div className="text-center mt-6">
              <Button asChild variant="ghost" className="text-primary"><Link to="/data-room">Request Data Room Access →</Link></Button>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
